import { useState, useCallback } from 'react'
import { Check, ChevronsUpDown, Loader2, Plane, Search } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { Badge } from '@/components/ui/badge'
import {
  useAircraftAutocomplete,
  useAircraftSearchFn,
  type AircraftAutocompleteResult,
  type ExternalAircraftData,
} from '@/hooks/useAircraftSearch'
import { AIRCRAFT_CATEGORY_LABELS, type AircraftCategory } from '@/types/aircraft-types'

interface AircraftSearchAutocompleteProps {
  /** Callback when an aircraft is selected and full data is fetched */
  onSelect: (aircraft: ExternalAircraftData) => void
  /** Disable the autocomplete */
  disabled?: boolean
  /** Placeholder text */
  placeholder?: string
  /** Custom class name */
  className?: string
}

/**
 * Aircraft search autocomplete component
 * Searches the AV8 API and returns full aircraft details on selection
 */
export function AircraftSearchAutocomplete({
  onSelect,
  disabled = false,
  placeholder = 'Search aircraft database...',
  className,
}: AircraftSearchAutocompleteProps) {
  const [open, setOpen] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedAircraft, setSelectedAircraft] = useState<AircraftAutocompleteResult | null>(null)
  const [isLoadingDetails, setIsLoadingDetails] = useState(false)

  // Debounced autocomplete search
  const { data: searchResults, isLoading: isSearching } = useAircraftAutocomplete(searchQuery, {
    enabled: open && searchQuery.length >= 2,
  })

  // Function to fetch full aircraft details
  const { getDetails } = useAircraftSearchFn()

  const handleSelect = useCallback(
    async (aircraft: AircraftAutocompleteResult) => {
      setSelectedAircraft(aircraft)
      setIsLoadingDetails(true)

      try {
        // Fetch full details from API
        const details = await getDetails(aircraft.id)
        if (details) {
          onSelect(details)
        }
      } catch (error) {
        console.error('Failed to fetch aircraft details:', error)
      } finally {
        setIsLoadingDetails(false)
        setOpen(false)
        setSearchQuery('')
      }
    },
    [getDetails, onSelect]
  )

  const getCategoryLabel = (category: string | undefined): string => {
    if (!category) return ''
    return AIRCRAFT_CATEGORY_LABELS[category as AircraftCategory] || category
  }

  const displayValue = selectedAircraft
    ? `${selectedAircraft.manufacturer} ${selectedAircraft.model}${selectedAircraft.variant ? ` ${selectedAircraft.variant}` : ''}`
    : ''

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled || isLoadingDetails}
          className={cn(
            'w-full justify-between font-normal',
            !selectedAircraft && 'text-muted-foreground',
            className
          )}
        >
          <div className="flex items-center gap-2 truncate">
            {isLoadingDetails ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Search className="h-4 w-4 shrink-0 opacity-50" />
            )}
            <span className="truncate">
              {isLoadingDetails ? 'Loading details...' : displayValue || placeholder}
            </span>
          </div>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[500px] p-0" align="start">
        <Command shouldFilter={false}>
          <CommandInput
            placeholder="Type to search (e.g., Citation CJ3, Gulfstream G650)..."
            value={searchQuery}
            onValueChange={setSearchQuery}
          />
          <CommandList>
            {isSearching && (
              <div className="flex items-center justify-center py-6 gap-2 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" />
                Searching...
              </div>
            )}

            {!isSearching && searchQuery.length >= 2 && (!searchResults || searchResults.length === 0) && (
              <CommandEmpty>
                No aircraft found for "{searchQuery}"
              </CommandEmpty>
            )}

            {!isSearching && searchQuery.length < 2 && (
              <div className="py-6 text-center text-sm text-muted-foreground">
                Type at least 2 characters to search
              </div>
            )}

            {!isSearching && searchResults && searchResults.length > 0 && (
              <CommandGroup heading="Search Results">
                {searchResults.map((aircraft) => (
                  <CommandItem
                    key={aircraft.id}
                    value={String(aircraft.id)}
                    onSelect={() => handleSelect(aircraft)}
                    className="flex items-center justify-between gap-2 cursor-pointer"
                  >
                    <div className="flex items-center gap-2 min-w-0">
                      <Plane className="h-4 w-4 shrink-0 text-muted-foreground" />
                      <div className="flex flex-col min-w-0">
                        <span className="font-medium truncate">
                          {aircraft.manufacturer} {aircraft.model}
                          {aircraft.variant && (
                            <span className="text-muted-foreground ml-1">
                              {aircraft.variant}
                            </span>
                          )}
                        </span>
                        {aircraft.icao_designator && (
                          <span className="text-xs text-muted-foreground">
                            ICAO: {aircraft.icao_designator}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      {aircraft.category && (
                        <Badge variant="secondary" className="text-xs">
                          {getCategoryLabel(aircraft.category)}
                        </Badge>
                      )}
                      {selectedAircraft?.id === aircraft.id && (
                        <Check className="h-4 w-4" />
                      )}
                    </div>
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}

/**
 * Inline search variant - shows results below input without popover
 * Useful for forms where you want the search always visible
 */
export function AircraftSearchInline({
  onSelect,
  disabled = false,
  placeholder = 'Search aircraft database...',
  className,
}: AircraftSearchAutocompleteProps) {
  const [searchQuery, setSearchQuery] = useState('')
  const [isLoadingDetails, setIsLoadingDetails] = useState(false)

  const { data: searchResults, isLoading: isSearching } = useAircraftAutocomplete(searchQuery)
  const { getDetails } = useAircraftSearchFn()

  const handleSelect = useCallback(
    async (aircraft: AircraftAutocompleteResult) => {
      setIsLoadingDetails(true)

      try {
        const details = await getDetails(aircraft.id)
        if (details) {
          onSelect(details)
          setSearchQuery('')
        }
      } catch (error) {
        console.error('Failed to fetch aircraft details:', error)
      } finally {
        setIsLoadingDetails(false)
      }
    },
    [getDetails, onSelect]
  )

  const getCategoryLabel = (category: string | undefined): string => {
    if (!category) return ''
    return AIRCRAFT_CATEGORY_LABELS[category as AircraftCategory] || category
  }

  return (
    <div className={cn('w-full', className)}>
      <Command className="rounded-lg border" shouldFilter={false}>
        <CommandInput
          placeholder={placeholder}
          value={searchQuery}
          onValueChange={setSearchQuery}
          disabled={disabled || isLoadingDetails}
        />
        {(isSearching || isLoadingDetails || (searchQuery.length >= 2 && searchResults)) && (
          <CommandList className="max-h-[200px]">
            {(isSearching || isLoadingDetails) && (
              <div className="flex items-center justify-center py-4 gap-2 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" />
                {isLoadingDetails ? 'Loading details...' : 'Searching...'}
              </div>
            )}

            {!isSearching && !isLoadingDetails && searchResults && searchResults.length === 0 && (
              <CommandEmpty>No aircraft found</CommandEmpty>
            )}

            {!isSearching && !isLoadingDetails && searchResults && searchResults.length > 0 && (
              <CommandGroup>
                {searchResults.map((aircraft) => (
                  <CommandItem
                    key={aircraft.id}
                    value={String(aircraft.id)}
                    onSelect={() => handleSelect(aircraft)}
                    className="flex items-center justify-between gap-2 cursor-pointer"
                  >
                    <div className="flex items-center gap-2 min-w-0">
                      <Plane className="h-4 w-4 shrink-0 text-muted-foreground" />
                      <span className="truncate">
                        {aircraft.manufacturer} {aircraft.model}
                        {aircraft.variant && ` ${aircraft.variant}`}
                      </span>
                    </div>
                    {aircraft.category && (
                      <Badge variant="secondary" className="text-xs shrink-0">
                        {getCategoryLabel(aircraft.category)}
                      </Badge>
                    )}
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
          </CommandList>
        )}
      </Command>
    </div>
  )
}
